import React, { useId } from 'react';
import LargeSophisticatedTooltipHeading from '../LargeSophisticatedTooltipHeading';
import SophisticatedTooltipText from '../SophisticatedTooltipText';
import s from './LargeSophisticatedTooltipContent.scss';

interface Props {
  /**
   * The descriptive text of the tooltip. Displays below the `heading`.
   *
   * This will get wrapped in a `<SophisticatedTooltipText />`
   */
  description?: React.ReactNode;

  /**
   * Displays at the top of the tooltip. Most specs put an image here.
   */
  prefix?: React.ReactNode;

  /**
   * Displays at the bottom of the tooltip. Consider supplying a `<SophisticatedTooltipLink />` here.
   */
  postfix?: React.ReactNode;
}

interface PropsWithHeading extends Props {
  /**
   * The heading of the tooltip. Displays above the `description`.
   *
   * This will get wrapped in a `<LargeSophisticatedTooltipHeading />`
   */
  heading: React.ReactNode;
}

interface PropsWithAriaLabel extends Props {
  'aria-label': string;
}

type CombinedProps = PropsWithHeading | PropsWithAriaLabel;

/**
 * Lays out the content for our large sophisticated tooltips.
 *
 * Either `heading` or `aria-label` is required so a11y software will know what the tooltip is for.
 */
const LargeSophisticatedTooltipContent: React.FC<CombinedProps> = ({
  description,
  prefix,
  postfix,
  ...labelOrHeading
}) => {
  // We won't always use this because we also accept `aria-label` in place of `heading` for a11y.
  const headingId = useId();

  const heading = (labelOrHeading as PropsWithHeading).heading;
  const ariaLabel = (labelOrHeading as PropsWithAriaLabel)['aria-label'];

  return (
    <div
      className={s['root']}
      role="dialog"
      aria-labelledby={heading ? headingId : undefined}
      aria-label={ariaLabel ? ariaLabel : undefined}
    >
      {prefix && <div className={s['prefix']}>{prefix}</div>}
      <div className={s['text-block']}>
        {heading && (
          <LargeSophisticatedTooltipHeading id={headingId}>
            {heading}
          </LargeSophisticatedTooltipHeading>
        )}
        {description && <SophisticatedTooltipText>{description}</SophisticatedTooltipText>}
        {postfix}
      </div>
    </div>
  );
};
LargeSophisticatedTooltipContent.displayName = 'LargeSophisticatedTooltipContent';

export default LargeSophisticatedTooltipContent;
